import React, {cloneElement, forwardRef, useEffect, useLayoutEffect, useRef, useState} from "react";
import {funcOrValOr} from "./CommonJS";
import {classNameScrollbarThin} from "./CommonHtml";
import {initRef, tailCheck, transitions} from "./CommonReact";
import {Button} from "./Inputs";
import {UIPopPanel} from "./UIPopPanel";


export const Menu = forwardRef(({items, dispRow, onSelect, className, classNameItm, itemHighlight, onPreSelect}, ref) => {
    const vals = useRef({}).current;

    function onLayout(el) {
        if (!el) return;
        vals.focusables = [...el.querySelectorAll(".dropdown-btn.cursor-pointer[tabindex]")];
        if (document.activeElement?.tagName !== "INPUT")        // quick hack not to steal focus from input elements.
            vals.focusables[0]?.focus();
        initRef(ref, el);
    }
    const moveTo = to => vals.focusables[to]?.focus();
    const move = (e, dir) => moveTo(vals.focusables.indexOf(e.target) + dir);

    return (
        <div ref={onLayout} className={className}>
            {funcOrValOr(items)?.map((itm, idx) => (
                itm.type === "sep" ? <div key={idx} className="border border-bg1"/>
                    : itm.type === "static" ?
                        <div key={idx} className={`flex rounded-md items-center w-full px-4 py-2 text-sm ${itm.className}`}>{itm.h}</div>
                        : <Button
                            key={idx} disabled={itm.disabled} onClick={e => {
                                e.stopPropagation();
                                onPreSelect?.();
                                (itm.a || onSelect)?.(itm, idx);
                            }} {...itm.props}
                            data-test={typeof itm.h === "string" ? itm.h.replace(/ /g, "-").toLowerCase() : `menu-item-${idx}`}
                            className={tailCheck(itm.className + " " + classNameItm, "mr-", "dropdown-btn", "rounded-md", "items-center gap-2",
                                                 "w-full", "px-3 py-1", itm.disabled ? "opacity-40 cursor-default" : "hover:bg-divider",
                                                 "focus-visible:bg-divider outline-none", itemHighlight === idx && "bg-prm-main/40")}
                            onKeys={{
                                ArrowUp: e => move(e, -1), ArrowDown: e => move(e, 1),
                                Home: () => moveTo(0), End: () => moveTo(vals.focusables.length - 1)
                            }}>
                            {dispRow?.(itm, idx) ?? <>
                                {typeof itm.i !== "string" ? itm.i : <span className={`${itm.i} opacity-70`}/>}
                                {itm.h}
                            </>}
                        </Button>
            ))}
        </div>
    );
});


export const PopupMenu = ({items = undefined, dispRow = undefined, onSelect = undefined, classNameItm = undefined, itemHighlight = undefined,
    refMenu = undefined, header = undefined, footer = undefined, classNamePanel = "", ...props}) =>
    <PopupPanel
        {...props} onShowPanel={({closePanel}) =>
            <div className={tailCheck(classNamePanel, "bg-popup fill-subtitle1 text-title", "rounded-md", "shadow-lg ring-opacity-5 focus:outline-none")}>
                {funcOrValOr(header)}
                <Menu
                    ref={refMenu} onPreSelect={closePanel}
                    className={`rounded-md overflow-auto max-h-[50vh] whitespace-pre ${classNameScrollbarThin}`}
                    {...{items, dispRow, classNameItm, itemHighlight, onSelect}}/>
                {funcOrValOr(footer)}
            </div>
        }/>;


// A button popping up a panel.
export function PopupPanel({button, align = "bottom-start", transition = transitions.wipeDownHide, margin = 10, onShowPanel, ctrl = undefined, ...props}) {
    const rAnchor = useRef(), locCtrl = useRef({}).current;
    ctrl ||= locCtrl;

    // add onClick and ref to provided button element
    return (<>
        {button && cloneElement(button, {onClick: e => {e.stopPropagation(); ctrl.set(cur => ({show: !cur.show}));}, ref: rAnchor})}
        <UIPopPanel {...{ctrl, rAnchor, align, transition, margin, ...props}} children={onShowPanel}/>
    </>);
}


export function OptsPicker({align = "bottom-start", opts, button, dispPopOpt, classNamePanel}) {
    function dispOptsPanel() {
        const vOpts = typeof opts !== "function" ? opts : opts();

        const dispOptsArr = opts =>
            <div className="p-1 flex items-center justify-between flex-wrap">
                {opts.map(opt => <div key={opt} className={`m-1.5`}>{dispPopOpt(opt)}</div>)}
            </div>;

        return typeof vOpts[0] !== "object" ? dispOptsArr(vOpts) :
            vOpts.map(({h, v}, i) =>
                <div key={i} className="flex flex-col opacity-90">
                    <div className="px-2 pt-1 text-sm text-white">{typeof h === "string" ? h + ":" : h}</div>
                    {dispOptsArr(v)}
                </div>
            );
    }

    return (
        <PopupPanel
            {...{button, align}} onShowPanel={() =>
                <div className={`overflow-y-auto max-h-72 whitespace-pre rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none ${(!classNamePanel?.match(/(^| )max-w-/)) && "max-w-2xl"} ${(!classNamePanel?.match(/(^| )bg-/)) && "bg-white dark:bg-gray-800/95 dark:text-gray-300"} ${classNamePanel}`}>
                    {dispOptsPanel()}
                </div>
            }/>
    );
}

const BTN_RESERVE = 36; // Approximate width of the +N button.
// Simulate flex-wrap layout from cached item widths to determine how many fit.
function calcFitCount(widths, itemH, gapX, gapY, maxH, containerW, forceBtn) {
    let x = 0, rowBottom = itemH, count = 0;
    for (let i = 0; i < widths.length; i++) {
        if (x > 0 && x + gapX + widths[i] > containerW) { x = 0; rowBottom += gapY + itemH; }
        if (rowBottom > maxH || widths[i] > containerW) break;
        x += (x > 0 ? gapX : 0) + widths[i];
        count++;
    }
    if (!forceBtn && count >= widths.length) return count; // All fit, no button needed (unless the caller always shows a "+N" button).
    // Reserve space for "+N" button — allow it to wrap to the next row if needed.
    while (count > 0) {
        let cx = 0, ry = itemH;
        for (let j = 0; j < count; j++) {
            if (cx > 0 && cx + gapX + widths[j] > containerW) { cx = 0; ry += gapY + itemH; }
            cx += (cx > 0 ? gapX : 0) + widths[j];
        }
        // Button fits on same row?
        if (cx + gapX + BTN_RESERVE <= containerW && ry <= maxH) return count;
        // Button wraps to next row?
        if (ry + gapY + itemH <= maxH) return count;
        count--;
    }
    // Never collapse to a lone "+N" — always show at least one (truncated) item when there is one.
    return widths.length ? 1 : 0;
}


/**
 * @param {() => void} [onMore] When set, the "+N" button calls it (e.g. opens a dialog) instead of popping up the hidden children.
 * @param {number} [totalCount] When set, "+N" reflects this larger total (totalCount - shown) rather than the hidden-children count — e.g. cveCount vs the 5 sample CVEs rendered.
 */
export function ReadMorePopUpWrapper({children: childrenRaw, numToShow = 3, autoFit = false, popPanelButton = undefined, classNameBtn = "", className = "", onItemClick = undefined,
    onMore = undefined, totalCount = undefined}) {
    const children = React.Children.toArray(childrenRaw);
    // Force the button so its slot is reserved even when all children fit (totalCount can still leave more to show).
    const forceBtn = totalCount != null && totalCount > children.length;
    const ref = useRef();
    const measured = useRef({widths: [], itemH: 0, gapX: 0, gapY: 0, maxH: 0});
    const [fitCount, setFitCount] = useState(null);

    // Measurement phase: capture natural item widths, gaps, and available height once.
    useLayoutEffect(() => {
        if (!autoFit || fitCount !== null || !ref.current) return;
        const el = ref.current;
        const nodes = Array.from(el.children);
        if (!nodes.length) return;
        // Prevent flex-shrink so items keep their natural width.
        nodes.forEach(n => n.style.flexShrink = "0");
        const style = getComputedStyle(el);
        const gapX = parseFloat(style.columnGap) || 0;
        const gapY = parseFloat(style.rowGap) || 0;
        const padY = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
        const widths = nodes.map(n => n.getBoundingClientRect().width);
        const itemH = nodes[0].getBoundingClientRect().height;
        nodes.forEach(n => n.style.flexShrink = "");
        // Collapse out of flow, walk up to find the row height set by sibling columns.
        const elWidth = el.offsetWidth;
        const prev = el.style.cssText;
        el.style.cssText = `position:absolute;visibility:hidden;width:${elWidth}px`;
        let maxH = itemH;
        for (let a = el.parentElement; a; a = a.parentElement)
            if (a.offsetHeight >= itemH) { maxH = a.offsetHeight - padY; break; }
        el.style.cssText = prev;
        measured.current = {widths, itemH, gapX, gapY, maxH};
        setFitCount(calcFitCount(widths, itemH, gapX, gapY, maxH, elWidth, forceBtn));
    });

    // Reset measurement when children count changes.
    useEffect(() => {
        if (autoFit && children.length !== measured.current.widths.length)
            setFitCount(null);
    }, [autoFit, children.length]);

    // Recalculate from cached widths on container resize — no re-render needed.
    useEffect(() => {
        if (!autoFit || !ref.current) return;
        let lastWidth = ref.current.offsetWidth;
        const observer = new ResizeObserver(entries => {
            const newWidth = entries[0].contentRect.width;
            if (Math.round(newWidth) !== Math.round(lastWidth)) {
                lastWidth = newWidth;
                const m = measured.current;
                if (m.widths.length)
                    setFitCount(calcFitCount(m.widths, m.itemH, m.gapX, m.gapY, m.maxH, newWidth, forceBtn));
            }
        });
        observer.observe(ref.current);
        return () => observer.disconnect();
    }, [autoFit]);

    // During the autofit measurement pass (fitCount still null) only the children may be in the DOM — a "+N" here would be measured as a child and corrupt the cached widths, so suppress it until fitCount is known.
    const measuring = autoFit && fitCount === null;
    const effectiveNum = autoFit ? (fitCount ?? children.length) : numToShow;
    const childrenToShow = children.slice(0, effectiveNum);
    const lastChild = childrenToShow.pop();
    // "+N" reflects the true remaining total when totalCount is given, else the number of hidden children.
    const moreCount = totalCount != null ? Math.max(totalCount - effectiveNum, 0) : children.length - effectiveNum;

    const moreBtn =
        <Button
            data-test="see-more-btn"
            onClick={onMore && (e => { e.stopPropagation(); onMore(); })}
            className={tailCheck(classNameBtn, "border-prm-light", "min-w-0", "shrink-0", "border", "text-prm-light",
                                 "border-dashed", "rounded-full", "shadow-lg", "px-1.5", "text-xs", "mr-")}>
            +{moreCount}
        </Button>;

    return (
        <div ref={ref} className={tailCheck(className, "items-center flex", "gap-x-1.5 gap-y-1", "flex-wrap", autoFit && "w-full h-full py-1 overflow-hidden")}>
            {childrenToShow}
            {lastChild}
            {moreCount > 0 && !measuring && (onMore
                ? moreBtn
                : <PopupPanel
                    button={popPanelButton ?? moreBtn}
                    onShowPanel={({closePanel}) =>
                        <div
                            className="bg-popup gap-1.5 rounded-lg p-1 flex flex-col" data-test="see-more-popup"
                            onClickCapture={onItemClick && closePanel}>
                            {children.slice(effectiveNum)}
                        </div>}/>)}
        </div>
    );
}
